import type { DoctorSubstituteDocumentApi } from '@omecare/utils';
import { FileCheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DoctorSubstituteDocumentRow } from './doctor-substitute-document-row';
import {
  listDoctorSubstituteDocuments,
  useDoctorSubstituteDocumentsViewModel,
} from './use-doctor-substitute-documents.viewmodel';

export type DoctorSubstituteDocumentsCardProps = {
  doctorSubstituteId: string;
  thesis: boolean;
  licenceDocument: DoctorSubstituteDocumentApi;
  rcpDocument: DoctorSubstituteDocumentApi;
  canView: boolean;
  canEdit: boolean;
  /** Render without the card chrome (e.g. inside a sheet). */
  plain?: boolean;
};

/**
 * "Documents" card of a substitute: the attestation RCP and, for a non-thésé,
 * the licence de remplacement, each with view and replace actions. Access is
 * enforced server-side; the flags only hide the controls.
 *
 * @param props - see {@link DoctorSubstituteDocumentsCardProps}
 * @returns the documents card
 */
export const DoctorSubstituteDocumentsCard = ({
  doctorSubstituteId,
  thesis,
  licenceDocument,
  rcpDocument,
  canView,
  canEdit,
  plain = false,
}: DoctorSubstituteDocumentsCardProps) => {
  const { t } = useTranslation();
  const viewModel = useDoctorSubstituteDocumentsViewModel({
    doctorSubstituteId,
  });
  const items = listDoctorSubstituteDocuments({
    thesis,
    licenceDocument,
    rcpDocument,
  });

  const list = (
    <ul className="grid gap-2">
      {items.map((item) => (
        <DoctorSubstituteDocumentRow
          key={item.type}
          item={item}
          viewModel={viewModel}
          canView={canView}
          canEdit={canEdit}
        />
      ))}
    </ul>
  );

  if (plain) {
    return (
      <div className="grid gap-3">
        <div className="font-semibold">
          {t('doctor-substitute-documents.title')}
        </div>
        {list}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 border-b">
        <CardTitle className="flex items-center gap-2.5 text-lg">
          <span className="flex size-9 items-center justify-center rounded-lg bg-primary/10">
            <FileCheckIcon className="size-4.5 text-primary" />
          </span>
          {t('doctor-substitute-documents.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">{list}</CardContent>
    </Card>
  );
};
